import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  Download,
  Trash2,
  RefreshCcw,
  ExternalLink,
  Mail,
  MailCheck,
  Loader2,
  Lock,
  Send,
  MailPlus,
  Repeat,
  AlertTriangle,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminApi, getToken, setToken, clearToken, API } from "@/lib/adminApi";
import { ApplicationsView } from "./AdminApplications";

const LOGO_URL = `${process.env.PUBLIC_URL || ""}/logo-somnium.png`;

const STATUS_LABELS = {
  new: "Nouvelle",
  contacted: "Contactée",
  accepted: "Acceptée",
  rejected: "Refusée",
};

const STATUS_STYLES = {
  new: "text-[#e6c98a] border-[#e6c98a]/40 bg-[#e6c98a]/5",
  contacted: "text-sky-300 border-sky-400/40 bg-sky-400/5",
  accepted: "text-emerald-300 border-emerald-400/40 bg-emerald-400/5",
  rejected: "text-rose-300 border-rose-400/40 bg-rose-400/5",
};

// ============================================================
// Login form
// ============================================================
const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await adminApi.post("/admin/login", { password });
      setToken(data.token);
      onSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Erreur d'authentification.";
      setError(typeof detail === "string" ? detail : "Erreur d'authentification.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-[100svh] bg-black flex items-center justify-center px-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-12">
          <img
            src={LOGO_URL}
            alt="Somnium Group"
            className="w-16 h-16 object-contain mb-5"
            draggable={false}
          />
          <div className="font-serif-display text-white text-lg tracking-mid">
            SOMNIUM GROUP
          </div>
          <div className="mt-3 text-[10px] tracking-luxe uppercase text-white/40">
            Espace privé · Admin
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label
              htmlFor="admin-password"
              className="text-[10px] tracking-luxe uppercase text-white/55"
            >
              Mot de passe
            </Label>
            <div className="relative">
              <Lock
                size={14}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30"
              />
              <Input
                id="admin-password"
                data-testid="admin-login-password"
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="luxe-input h-12 pl-10 pr-4"
                required
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="text-sm text-red-300/90 border border-red-300/20 rounded px-4 py-3 bg-red-950/20"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="luxe-btn w-full"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Connexion…
              </>
            ) : (
              "Entrer"
            )}
          </button>
        </form>

        <div className="mt-10 text-center text-[10px] tracking-luxe uppercase text-white/30">
          Privé · Structuré · Vérifié
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Dashboard
// ============================================================
const StatCard = ({ label, value, accent }) => (
  <div className="px-5 py-4 border border-white/10 rounded-md bg-white/[0.02]">
    <div className="text-[10px] tracking-luxe uppercase text-white/45">
      {label}
    </div>
    <div
      className={`mt-2 font-serif-display text-3xl ${
        accent ? "text-[#e6c98a]" : "text-white"
      }`}
    >
      {value}
    </div>
  </div>
);

const CONT_STATUS_LABELS = {
  sent: "Envoyé",
  failed: "Échec",
  not_sent: "Non envoyé",
  pending: "En cours",
};

const CONT_STATUS_STYLES = {
  sent: "text-emerald-300 border-emerald-400/40 bg-emerald-400/5",
  failed: "text-rose-300 border-rose-400/40 bg-rose-400/5",
  not_sent: "text-white/60 border-white/15 bg-white/5",
  pending: "text-sky-300 border-sky-400/40 bg-sky-400/5",
};

const RequestRow = ({ req, onChangeStatus, onDelete, onSendWelcome, onResendContinuation, resendingId }) => {
  const date = new Date(req.timestamp);
  const dateStr = date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const welcomeSent = !!req.welcome_sent_at;
  const contStatus = req.continuation_email_status || "not_sent";
  const contSentAt = req.continuation_email_sent_at
    ? new Date(req.continuation_email_sent_at).toLocaleString("fr-FR", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : null;
  const isResending = resendingId === req.id;
  return (
    <tr
      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
      data-testid={`admin-row-${req.id}`}
    >
      <td className="px-4 py-4 align-top">
        <div className="text-white text-sm flex items-center gap-2">
          {req.full_name}
          {req.invitation_code ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] tracking-luxe uppercase bg-[#e6c98a]/10 text-[#e6c98a] border border-[#e6c98a]/30"
              title={`Code d'invitation : ${req.invitation_code}`}
              data-testid={`admin-row-invited-${req.id}`}
            >
              Invité
            </span>
          ) : null}
        </div>
        <div className="text-white/40 text-xs mt-1">{dateStr}</div>
        {req.application_reference && (
          <div className="text-white/30 text-[10px] tracking-mid mt-1">
            {req.application_reference}
          </div>
        )}
      </td>
      <td className="px-4 py-4 align-top">
        <a
          href={`mailto:${req.email}`}
          className="text-white/80 hover:text-[#e6c98a] text-sm break-all"
        >
          {req.email}
        </a>
        {req.linkedin && (
          <a
            href={req.linkedin.startsWith("http") ? req.linkedin : `https://${req.linkedin}`}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-white/40 hover:text-white/70 text-xs"
          >
            LinkedIn <ExternalLink size={10} />
          </a>
        )}
      </td>
      <td className="px-4 py-4 align-top">
        <div className="text-white/75 text-xs uppercase tracking-mid">
          {req.profile}
        </div>
        {req.invitation_code && (
          <div className="mt-1 text-[10px] text-[#e6c98a]/80 tracking-mid">
            code: {req.invitation_code}
          </div>
        )}
      </td>
      <td className="px-4 py-4 align-top">
        <div
          className={`inline-flex flex-col items-start gap-1 px-2 py-1 rounded border text-[10px] tracking-mid uppercase ${CONT_STATUS_STYLES[contStatus]}`}
          data-testid={`admin-row-cont-status-${req.id}`}
        >
          <span className="inline-flex items-center gap-1">
            {contStatus === "sent" && <MailCheck size={12} />}
            {contStatus === "failed" && <AlertTriangle size={12} />}
            {contStatus === "not_sent" && <Mail size={12} />}
            {CONT_STATUS_LABELS[contStatus]}
          </span>
          {contStatus === "sent" && contSentAt && (
            <span className="text-[9px] normal-case tracking-normal opacity-80">
              {contSentAt}
            </span>
          )}
        </div>
        {contStatus === "failed" && req.continuation_email_error && (
          <div
            className="mt-1 text-[10px] text-rose-300/70 max-w-[220px] line-clamp-2"
            title={req.continuation_email_error}
          >
            {req.continuation_email_error}
          </div>
        )}
      </td>
      <td className="px-4 py-4 align-top">
        <Select
          value={req.status || "new"}
          onValueChange={(v) => onChangeStatus(req, v)}
        >
          <SelectTrigger
            data-testid={`admin-row-status-${req.id}`}
            className={`luxe-input h-9 px-3 text-xs uppercase tracking-mid w-[150px] ${
              STATUS_STYLES[req.status || "new"]
            }`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-neutral-950 border-white/10 text-white">
            {Object.entries(STATUS_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>
                {l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-4 py-4 align-top text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onResendContinuation(req)}
            disabled={isResending}
            className={`p-2 rounded border transition-colors ${
              contStatus === "failed"
                ? "border-rose-300/40 text-rose-300 hover:text-black hover:bg-rose-300 hover:border-rose-300"
                : "border-white/15 text-white/60 hover:text-[#e6c98a] hover:border-[#e6c98a]/50"
            } ${isResending ? "opacity-50 cursor-wait" : ""}`}
            title={
              contStatus === "failed"
                ? `Renvoyer le mail de continuation (échec précédent : ${req.continuation_email_error || "?"})`
                : contStatus === "sent"
                ? "Renvoyer le mail de continuation"
                : "Envoyer le mail de continuation"
            }
            data-testid={`admin-row-resend-${req.id}`}
          >
            {isResending ? <Loader2 size={14} className="animate-spin" /> : <Repeat size={14} />}
          </button>
          <button
            type="button"
            onClick={() => onSendWelcome(req)}
            disabled={welcomeSent}
            className={`p-2 rounded border transition-colors ${
              welcomeSent
                ? "border-emerald-400/30 text-emerald-300/80 cursor-not-allowed"
                : "border-[#e6c98a]/30 text-[#e6c98a] hover:text-black hover:bg-[#e6c98a] hover:border-[#e6c98a]"
            }`}
            title={
              welcomeSent
                ? "Email de bienvenue déjà envoyé"
                : "Envoyer l'email de bienvenue (valide la candidature)"
            }
            data-testid={`admin-row-welcome-${req.id}`}
          >
            {welcomeSent ? <MailCheck size={14} /> : <Send size={14} />}
          </button>
          <a
            href={`mailto:${req.email}`}
            className="p-2 rounded border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-colors"
            title="Répondre par email"
            data-testid={`admin-row-reply-${req.id}`}
          >
            <Mail size={14} />
          </a>
          <button
            type="button"
            onClick={() => onDelete(req)}
            className="p-2 rounded border border-white/10 text-white/40 hover:text-rose-300 hover:border-rose-300/40 transition-colors"
            title="Supprimer"
            data-testid={`admin-row-delete-${req.id}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

const AdminDashboard = ({ onLogout }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    accepted: 0,
    rejected: 0,
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [invitedOnly, setInvitedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [toWelcome, setToWelcome] = useState(null);
  const [sendingWelcome, setSendingWelcome] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkMode, setBulkMode] = useState("normal"); // normal | force | only_failed
  const [resendingId, setResendingId] = useState(null);
  const [activeTab, setActiveTab] = useState("requests"); // requests | applications

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const [reqRes, statsRes] = await Promise.all([
        adminApi.get("/admin/requests", { params }),
        adminApi.get("/admin/stats"),
      ]);
      const filtered = invitedOnly
        ? reqRes.data.filter((r) => !!r.invitation_code)
        : reqRes.data;
      setItems(filtered);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error("Impossible de charger les demandes.");
      }
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, invitedOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChangeStatus = async (req, newStatus) => {
    const prev = items;
    setItems((arr) =>
      arr.map((r) => (r.id === req.id ? { ...r, status: newStatus } : r))
    );
    try {
      await adminApi.patch(`/admin/requests/${req.id}`, { status: newStatus });
      toast.success(`Statut mis à jour : ${STATUS_LABELS[newStatus]}`);
      // refresh stats
      const { data } = await adminApi.get("/admin/stats");
      setStats(data);
    } catch (err) {
      setItems(prev);
      toast.error("Échec de la mise à jour.");
    }
  };

  const handleConfirmDelete = async () => {
    if (!toDelete) return;
    try {
      await adminApi.delete(`/admin/requests/${toDelete.id}`);
      toast.success("Demande supprimée.");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error("Échec de la suppression.");
    }
  };

  const handleExport = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/admin/requests.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `somnium-access-requests-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export impossible.");
    }
  };

  const handleConfirmWelcome = async () => {
    if (!toWelcome) return;
    setSendingWelcome(true);
    try {
      const { data } = await adminApi.post(
        `/admin/requests/${toWelcome.id}/send-welcome`
      );
      toast.success(`Email de bienvenue envoyé à ${data.email}.`);
      setItems((arr) => arr.map((r) => (r.id === data.id ? data : r)));
      const stats = await adminApi.get("/admin/stats");
      setStats(stats.data);
      setToWelcome(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Envoi impossible.";
      toast.error(typeof detail === "string" ? detail : "Envoi impossible.");
    } finally {
      setSendingWelcome(false);
    }
  };

  const handleResendContinuation = async (req) => {
    setResendingId(req.id);
    try {
      const { data } = await adminApi.post(`/admin/requests/${req.id}/resend-continuation`);
      toast.success(`Mail de continuation envoyé à ${data.email}.`);
      // Optimistically update the row
      setItems((arr) =>
        arr.map((r) =>
          r.id === req.id
            ? {
                ...r,
                continuation_email_status: "sent",
                continuation_email_sent_at: data.continuation_email_sent_at,
                continuation_email_error: null,
                application_reference: data.application_reference || r.application_reference,
              }
            : r
        )
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || "Envoi impossible.";
      toast.error(typeof detail === "string" ? detail : "Envoi impossible.");
      // Reflect failure in the row
      setItems((arr) =>
        arr.map((r) =>
          r.id === req.id
            ? { ...r, continuation_email_status: "failed", continuation_email_error: typeof detail === "string" ? detail : "" }
            : r
        )
      );
    } finally {
      setResendingId(null);
    }
  };

  const handleBulkContinuation = async () => {
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const params = {};
      if (bulkMode === "force") params.force = true;
      if (bulkMode === "only_failed") params.only_failed = true;
      const { data } = await adminApi.post(`/admin/bulk-send-continuation`, null, { params });
      // Poll status every 2s until done/error
      const poll = async () => {
        try {
          const { data: st } = await adminApi.get(`/admin/bulk-send-status`);
          setBulkResult(st);
          if (st?.status === "running") {
            setTimeout(poll, 2000);
          } else {
            setBulkRunning(false);
            if (st?.status === "done") {
              const msg = `Envoyés : ${st.sent} · Échecs : ${st.failed} · Ignorés : ${st.skipped}`;
              if ((st.failed || 0) === 0) toast.success(msg);
              else toast.warning(msg);
              // Refresh list to reflect new statuses
              load();
            } else if (st?.status === "error") {
              toast.error(st.error || "Le job a échoué.");
            }
          }
        } catch {
          setBulkRunning(false);
        }
      };
      // Show initial started state
      setBulkResult({ status: "running", ...data, sent: 0, failed: 0, skipped: 0, total: 0 });
      setTimeout(poll, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Envoi impossible.";
      toast.error(typeof detail === "string" ? detail : "Envoi impossible.");
      setBulkRunning(false);
    }
  };

  const filteredCount = items.length;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-sm bg-black/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Somnium Group"
              className="w-7 h-7 object-contain opacity-90"
              draggable={false}
            />
            <div>
              <div className="font-serif-display text-white text-sm tracking-mid">
                SOMNIUM GROUP
              </div>
              <div className="text-[9px] tracking-luxe uppercase text-white/40">
                Espace admin
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh"
              className="p-2 rounded border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-colors"
              title="Rafraîchir"
            >
              <RefreshCcw size={14} />
            </button>
            <button
              type="button"
              onClick={() => { setBulkResult(null); setBulkOpen(true); }}
              data-testid="admin-bulk-continuation"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-mid uppercase rounded border border-[#e6c98a]/40 hover:border-[#e6c98a] hover:text-[#e6c98a] text-white/80 transition-colors"
              title="Envoyer l'email de continuation à toutes les demandes existantes"
            >
              <MailPlus size={14} /> Emails de continuation
            </button>
            <button
              type="button"
              onClick={handleExport}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-mid uppercase rounded border border-white/15 hover:border-[#e6c98a]/50 hover:text-[#e6c98a] text-white/80 transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-mid uppercase rounded border border-white/15 hover:border-white/40 text-white/80 transition-colors"
            >
              <LogOut size={14} /> Quitter
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-white/10 mb-8">
          <button
            type="button"
            onClick={() => setActiveTab("requests")}
            data-testid="admin-tab-requests"
            className={`pb-3 -mb-px text-xs tracking-luxe uppercase border-b-2 transition-colors ${
              activeTab === "requests"
                ? "border-[#e6c98a] text-[#e6c98a]"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >
            Demandes d&apos;accès
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("applications")}
            data-testid="admin-tab-applications"
            className={`pb-3 -mb-px text-xs tracking-luxe uppercase border-b-2 transition-colors ${
              activeTab === "applications"
                ? "border-[#e6c98a] text-[#e6c98a]"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >
            Candidatures &amp; funnel
          </button>
        </div>

        {activeTab === "applications" ? (
          <ApplicationsView />
        ) : (
          <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <StatCard label="Total" value={stats.total} accent />
          <StatCard label="Nouvelles" value={stats.new} />
          <StatCard label="Contactées" value={stats.contacted} />
          <StatCard label="Acceptées" value={stats.accepted} />
          <StatCard label="Refusées" value={stats.rejected} />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30"
            />
            <Input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, email, profil…"
              className="luxe-input h-11 pl-10 pr-4"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="admin-status-filter"
              className="luxe-input h-11 px-4 w-full md:w-[200px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-neutral-950 border-white/10 text-white">
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => setInvitedOnly((v) => !v)}
            data-testid="admin-invited-toggle"
            className={`h-11 px-4 rounded text-xs tracking-mid uppercase border transition-colors whitespace-nowrap ${
              invitedOnly
                ? "border-[#e6c98a]/60 bg-[#e6c98a]/10 text-[#e6c98a]"
                : "border-white/15 text-white/60 hover:border-white/30 hover:text-white"
            }`}
          >
            Invités seulement
          </button>
        </div>

        <div className="mb-3 text-[10px] tracking-luxe uppercase text-white/40">
          {filteredCount} demande{filteredCount > 1 ? "s" : ""}
        </div>

        {/* Table */}
        <div className="border border-white/10 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-white/[0.03] border-b border-white/10">
                <tr className="text-[10px] tracking-luxe uppercase text-white/45">
                  <th className="px-4 py-3 font-normal">Demandeur</th>
                  <th className="px-4 py-3 font-normal">Contact</th>
                  <th className="px-4 py-3 font-normal">Profil</th>
                  <th className="px-4 py-3 font-normal">Continuation</th>
                  <th className="px-4 py-3 font-normal">Statut</th>
                  <th className="px-4 py-3 font-normal text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center text-white/40">
                      <Loader2 className="animate-spin inline mr-2" size={16} />
                      Chargement…
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-16 text-center text-white/40"
                      data-testid="admin-empty"
                    >
                      Aucune demande à afficher.
                    </td>
                  </tr>
                ) : (
                  items.map((req) => (
                    <RequestRow
                      key={req.id}
                      req={req}
                      onChangeStatus={handleChangeStatus}
                      onDelete={(r) => setToDelete(r)}
                      onSendWelcome={(r) => setToWelcome(r)}
                      onResendContinuation={handleResendContinuation}
                      resendingId={resendingId}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-neutral-950 border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-xl">
              Supprimer cette demande ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Cette action est irréversible. La demande de
              <span className="text-white"> {toDelete?.full_name}</span> sera
              définitivement effacée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              data-testid="admin-delete-confirm"
              className="bg-rose-500/80 hover:bg-rose-500 text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!toWelcome}
        onOpenChange={(o) => !sendingWelcome && !o && setToWelcome(null)}
      >        <AlertDialogContent className="bg-neutral-950 border-white/10 text-white max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-xl">
              Envoyer l&apos;email de bienvenue ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Un email de bienvenue Somnium Group sera envoyé à
              <span className="text-white"> {toWelcome?.email}</span> (
              {toWelcome?.locale === "en" ? "EN" : "FR"}). La demande passera
              automatiquement en statut <span className="text-emerald-300">Acceptée</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded border border-white/10 bg-black/40 p-4 text-xs text-white/70 leading-relaxed">
            <div className="text-[10px] tracking-luxe uppercase text-white/40 mb-2">
              Aperçu
            </div>
            <div className="font-serif-display text-[#e6c98a] text-sm mb-1">
              {toWelcome?.locale === "en" ? "Welcome to Somnium Group" : "Bienvenue chez Somnium Group"}
            </div>
            <div>
              {toWelcome?.locale === "en"
                ? `Welcome ${toWelcome?.full_name?.split(" ")[0] || ""},`
                : `Bienvenue ${toWelcome?.full_name?.split(" ")[0] || ""},`}
              <br />
              {toWelcome?.locale === "en"
                ? "Your request to join Somnium Group has been validated. Our team will reach out with your credentials as soon as the application is ready."
                : "Votre demande d'accès à Somnium Group a été validée. Notre équipe reviendra vers vous avec vos accès dès que l'application sera prête."}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={sendingWelcome}
              className="bg-transparent border-white/15 text-white hover:bg-white/5"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmWelcome}
              disabled={sendingWelcome}
              data-testid="admin-welcome-confirm"
              className="bg-[#e6c98a] hover:bg-[#d4b46d] text-black"
            >
              {sendingWelcome ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-2" /> Envoi…
                </>
              ) : (
                "Envoyer la bienvenue"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkOpen} onOpenChange={(o) => !bulkRunning && setBulkOpen(o)}>
        <AlertDialogContent className="bg-neutral-950 border-white/10 text-white max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-xl">
              Envoyer les emails de continuation ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Un email « Continue my application » sera envoyé aux demandes existantes avec un lien magique unique valable 30 jours.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {!bulkResult && (
            <div className="space-y-2 pt-1">
              <div className="text-[10px] tracking-luxe uppercase text-white/45 mb-2">Mode d&apos;envoi</div>
              {[
                { v: "normal", t: "Nouveaux uniquement", d: "Ignore les emails déjà envoyés." },
                { v: "only_failed", t: "Renvoyer les échecs", d: "Uniquement les envois qui ont échoué précédemment." },
                { v: "force", t: "Forcer l'envoi à TOUS", d: "Renvoie à tout le monde même si déjà envoyé (peut créer des doublons)." },
              ].map((m) => (
                <label
                  key={m.v}
                  className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors ${
                    bulkMode === m.v
                      ? "border-[#e6c98a]/60 bg-[#e6c98a]/5"
                      : "border-white/10 hover:border-white/25"
                  }`}
                  data-testid={`admin-bulk-mode-${m.v}`}
                >
                  <input
                    type="radio"
                    name="bulk-mode"
                    className="mt-1 accent-[#e6c98a]"
                    checked={bulkMode === m.v}
                    onChange={() => setBulkMode(m.v)}
                  />
                  <div>
                    <div className="text-sm text-white">{m.t}</div>
                    <div className="text-xs text-white/50 mt-0.5">{m.d}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {bulkResult && (
            <div className="rounded border border-white/10 bg-black/40 p-4 text-sm text-white/80">
              <div className="grid grid-cols-2 gap-3">
                <div>Envoyés : <span className="text-[#e6c98a]">{bulkResult.sent}</span></div>
                <div>Échecs : <span className="text-rose-300">{bulkResult.failed}</span></div>
                <div>Ignorés : <span className="text-white/60">{bulkResult.skipped}</span></div>
                <div>Total : <span className="text-white">{bulkResult.total}</span></div>
              </div>
              {bulkResult.errors?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="text-[10px] tracking-luxe uppercase text-rose-300/80 mb-2">Erreurs</div>
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {bulkResult.errors.map((e, i) => (
                      <li key={i} className="text-xs text-white/60">
                        <span className="text-white/80">{e.email}</span> — {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={bulkRunning}
              className="bg-transparent border-white/15 text-white hover:bg-white/5"
            >
              Fermer
            </AlertDialogCancel>
            {(!bulkResult || bulkResult.status !== "running") && (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); handleBulkContinuation(); }}
                disabled={bulkRunning}
                data-testid="admin-bulk-confirm"
                className="bg-[#e6c98a] hover:bg-[#d4b46d] text-black"
              >
                {bulkRunning ? (
                  <><Loader2 size={14} className="animate-spin mr-2" /> Envoi en cours…</>
                ) : (
                  bulkResult ? "Relancer" : (bulkMode === "force" ? "Forcer l'envoi à tous" : "Envoyer")
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============================================================
// Page wrapper
// ============================================================
export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(!!getToken());

  useEffect(() => {
    document.title = "Somnium Group — Admin";
    if (!getToken()) {
      setChecking(false);
      return;
    }
    let mounted = true;
    adminApi
      .get("/admin/me")
      .then(() => mounted && setAuthed(true))
      .catch(() => mounted && setAuthed(false))
      .finally(() => mounted && setChecking(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (checking) {
    return (
      <div className="min-h-[100svh] bg-black flex items-center justify-center text-white/40">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }

  return (
    <AdminDashboard
      onLogout={() => {
        clearToken();
        setAuthed(false);
      }}
    />
  );
}
